export const QUEST_LISTING_PATH = "/quests";

export const CATEGORY_LABELS = {
  quests: "Quest",
  events: "Event",
  showcases: "Showcase",
};

export const stripHtml = (value = "") =>
  String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();

export const formatQuestDate = (value) => {
  if (!value) return "TBA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "TBA";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatQuestDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "Schedule TBA";
  if (!startDate) return `Closes ${formatQuestDate(endDate)}`;
  if (!endDate) return `Opens ${formatQuestDate(startDate)}`;
  return `${formatQuestDate(startDate)} - ${formatQuestDate(endDate)}`;
};

export const formatEntryFee = (entryFeeCents = 0, currency = "usd") => {
  const cents = Number(entryFeeCents) || 0;
  if (cents <= 0) return "Free entry";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format(cents / 100);
};

export const formatAccessList = (accessibility = []) => {
  if (!Array.isArray(accessibility) || accessibility.length === 0) return ["Members"];
  return accessibility.map((item) => {
    const text = String(item || "").trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Members";
  });
};

export const getQuestStatus = (item = {}) => {
  const now = new Date();
  const start = item.startDate ? new Date(item.startDate) : null;
  const end = item.endDate ? new Date(item.endDate) : null;

  if (start && !Number.isNaN(start.getTime()) && start > now) {
    return { label: "Upcoming", className: "bg-sky-50 text-sky-700 border-sky-200" };
  }

  if (end && !Number.isNaN(end.getTime()) && end < now) {
    return { label: "Past", className: "bg-gray-100 text-gray-600 border-gray-200" };
  }

  return { label: "Open now", className: "bg-emerald-50 text-emerald-700 border-emerald-200" };
};

export const getDescriptionLines = (description = "") =>
  stripHtml(description)
    .split(/\r?\n|(?<=\.)\s+(?=[A-Z][A-Za-z ]{2,}:)/)
    .map((line) => line.trim())
    .filter(Boolean);

export const extractQuestFields = (description = "") => {
  const fields = [];
  const bodyLines = [];

  getDescriptionLines(description).forEach((line) => {
    const match = line.match(/^([A-Za-z][A-Za-z /&-]{2,28})\s*:\s*(.+)$/);
    if (match) {
      const label = match[1].trim();
      const value = match[2].trim();
      if (value) fields.push({ label, value });
    } else {
      bodyLines.push(line);
    }
  });

  return { fields, body: bodyLines.join(" ") };
};

export const getQuestImages = (item = {}) => {
  const images = Array.isArray(item.images) ? item.images : [];
  return [...images, item.image, item.secondaryImage]
    .map((image) => String(image || "").trim())
    .filter(Boolean)
    .filter((image, index, list) => list.indexOf(image) === index);
};

export const getQuestBrief = (description = "") => {
  const { fields, body } = extractQuestFields(description);
  const paragraphs = (body || stripHtml(description))
    .split(/\n{2,}|(?<=\.)\s+(?=[A-Z])/)
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    paragraphs,
    fields,
  };
};

const QUEST_EVENT_HEADINGS = [
  { key: "welcome", label: "Welcome Message", patterns: ["Welcome Message"] },
  { key: "brief", label: "Brief of the Challenge", patterns: ["Brief of the Challenge", "Challenge Brief", "Brief"] },
  { key: "prompt", label: "Challenge Prompt", patterns: ["The challenge prompt is", "Challenge Prompt", "Prompt"] },
  { key: "eligibility", label: "Eligibility", patterns: ["Eligibility"] },
  { key: "examples", label: "Example Subjects", patterns: ["Example Subjects", "Examples"] },
  { key: "story", label: "Storytelling Guidance", patterns: ["What Story Could the Images Convey?", "What Story Could the Images Convey", "Storytelling Guidance"] },
  { key: "technical", label: "Technical Tips", patterns: ["Technical Tips"] },
  { key: "judging", label: "How Submissions Might Be Judged", patterns: ["How Submissions Might Be Judged", "Judging Criteria"] },
  { key: "information", label: "Other Useful Information", patterns: ["Other Useful Information"] },
  { key: "rules", label: "Rules", patterns: ["Rules"] },
  { key: "rights", label: "Rights and License", patterns: ["By submitting your entry", "Usage License", "Rights / License"] },
];

const QUEST_EVENT_SUBHEADINGS = [
  "Cultural Street Art",
  "Traditional Urban Markets",
  "Lighting and Timing",
  "Composition",
  "Post-Processing",
  "Safety First",
  "Creativity and Originality",
  "Relevance to Theme",
  "Technical Quality",
  "Storytelling and Impact",
  "Cultural Sensitivity",
  "Eligibility and Submission Process",
  "Deadline",
  "Prizes and Perks",
  "Resources",
];

const escapeRegExp = (value = "") => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getSectionMatches = (text = "") => {
  const matches = [];

  QUEST_EVENT_HEADINGS.forEach((heading) => {
    heading.patterns.forEach((pattern) => {
      const regex = new RegExp(`(^|\\n|\\s)(${escapeRegExp(pattern)})(\\s*:)?`, "i");
      const match = regex.exec(text);
      if (match) {
        const index = match.index + (match[1] ? match[1].length : 0);
        matches.push({
          ...heading,
          index,
          end: index + match[2].length + (match[3] ? match[3].length : 0),
          matchedLabel: match[2],
        });
      }
    });
  });

  return matches
    .sort((a, b) => a.index - b.index)
    .filter((match, index, list) => list.findIndex((item) => item.key === match.key) === index);
};

const prepareQuestSectionText = (value = "") => {
  let text = String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  QUEST_EVENT_SUBHEADINGS.forEach((heading) => {
    const regex = new RegExp(`[ \\t]+(${escapeRegExp(heading)})([ \\t]*:)`, "g");
    text = text.replace(regex, "\n$1$2");
  });

  text = text.replace(/\s+(Accepted file formats|All photos must|No AI-generated|A model release|A property release|Photos must|Tag your location|Photos should|Ensure images)/g, "\n$1");
  return text;
};

const splitQuestSectionBody = (value = "") => {
  const prepared = prepareQuestSectionText(value);
  const lines = prepared
    .split(/\n{2,}/)
    .map((line) => line.trim())
    .filter(Boolean);

  const items = [];
  const paragraphs = [];

  lines.forEach((line) => {
    const itemMatch = line.match(/^([^:]{3,90}):\s*(.+)$/);
    if (itemMatch && !/^Dear /i.test(line)) {
      items.push({
        title: itemMatch[1].trim(),
        body: itemMatch[2].trim(),
      });
    } else {
      paragraphs.push(line);
    }
  });

  return { paragraphs, items };
};

// Structured quest fields already have an explicit section boundary. Parsing
// them as a complete quest can mistake words such as "Eligibility" or
// "Rules" inside the field for a new top-level section and drop the remaining
// content when callers select the first parsed result.
export const parseQuestSectionContent = (value = "") =>
  splitQuestSectionBody(stripHtml(value));

export const parseQuestEventFlow = (description = "") => {
  const text = stripHtml(description)
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (!text) return [];

  const matches = getSectionMatches(text);
  if (!matches.length) {
    const { paragraphs, items } = splitQuestSectionBody(text);
    return [{ key: "overview", label: "Quest Brief", paragraphs, items }];
  }

  const sections = [];

  const firstMatch = matches[0];
  const intro = text.slice(0, firstMatch.index).trim();
  if (intro) {
    const { paragraphs, items } = splitQuestSectionBody(intro);
    sections.push({ key: "intro", label: "Quest Overview", paragraphs, items });
  }

  matches.forEach((match, index) => {
    const next = matches[index + 1];
    const body = text.slice(match.end, next ? next.index : text.length).trim();
    const { paragraphs, items } = splitQuestSectionBody(body);
    if (paragraphs.length || items.length) {
      sections.push({
        key: match.key,
        label: match.label,
        paragraphs,
        items,
      });
    }
  });

  return sections;
};

export const getQuestSummary = (description = "", maxLength = 240) => {
  const { body } = extractQuestFields(description);
  const summary = body || stripHtml(description);
  if (summary.length <= maxLength) return summary;
  return `${summary.slice(0, maxLength).trim()}...`;
};

const LONG_PROMPT_LABELS = [
  "challenge prompt",
  "prompt",
  "description",
  "overview",
  "brief",
  "instructions",
  "guidelines",
  "requirements",
  "submission guidelines",
  "judging criteria",
];

export const getCompactQuestFields = (description = "", limit = 2) =>
  extractQuestFields(description).fields
    .filter((field) => {
      const label = field.label.toLowerCase();
      const value = String(field.value || "").trim();
      return (
        value.length > 0 &&
        value.length <= 90 &&
        !LONG_PROMPT_LABELS.some((blocked) => label.includes(blocked))
      );
    })
    .slice(0, limit);
